import { atlasContract } from '@atlas/contracts';
import { tsRestFetchApi, type ApiFetcher, type ApiFetcherArgs } from '@ts-rest/core';
import { initQueryClient } from '@ts-rest/react-query';

import { refreshSession } from '@/lib/auth';
import { useAuthStore } from '@/lib/auth-store';
import { queryClient } from '@/lib/query-client';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3100';

/**
 * A HÍVÁS-BURKOLÓ: Bearer-injektálás a store-ból, 401-nél EGYSZER csendes refresh (httpOnly cookie) +
 * retry — így a lejárt access-token nem dobja ki a felhasználót munka közben.
 *
 * MIÉRT KÜLÖN, EXPORTÁLT FÜGGVÉNY (és nem inline lambda a kliens-konfigban): ez a négy sor a felület
 * munkamenet-viselkedésének a szíve (kiléptet vagy bent tart), és inline lambdaként NEM volt tesztelhető.
 * A `fetcher` paraméter csak a tesztek kedvéért létezik, alapértelmezése a ts-rest valódi fetch-e — a
 * futásidejű viselkedés bitre változatlan.
 */
export async function authedApiFetch(
  args: ApiFetcherArgs,
  fetcher: ApiFetcher = tsRestFetchApi,
): ReturnType<ApiFetcher> {
  const token = useAuthStore.getState().token;
  if (token) {
    args.headers.authorization = `Bearer ${token}`;
  }
  const res = await fetcher(args);
  // Lejárt/érvénytelen access-token → csendes refresh + egyszeri retry (az auth-végpontokra NEM: ott a 401
  // maga a válasz, és egy retry végtelen refresh-hurkot nyitna).
  if (res.status === 401 && !args.path.includes('/auth/')) {
    const outcome = await refreshSession();
    if (outcome === 'ok') {
      const fresh = useAuthStore.getState().token;
      if (fresh) args.headers.authorization = `Bearer ${fresh}`;
      return fetcher(args);
    }
    // CSAK a VALÓDI munkamenet-vég léptet ki. Átmeneti zavarra (`transient`: a BFF 503-a Odin-hibára/fojtásra,
    // a limiter 429-e, hálózati hiba) a refresh-cookie ÉRVÉNYES marad — a kiléptetés ilyenkor adatvesztés és
    // fölösleges újra-belépés, ami az Odin fiókonkénti login-limitjébe (5/15 perc) fut. A hívó ekkor az
    // EREDETI 401-et kapja: a lap hibát mutat, a munkamenet viszont él, és a következő próbálkozás megy.
    if (outcome === 'unauthorized') {
      useAuthStore.getState().logout(); // nincs munkamenet → tiszta állapot (a route-guard loginra visz)
      queryClient.clear(); // a privát cache nem marad a következő bejelentkezőnek
    }
  }
  return res;
}

/**
 * Tipizált ts-rest + react-query kliens az Atlas app-backendhez (@atlas/contracts).
 */
export const api = initQueryClient(atlasContract, {
  baseUrl,
  baseHeaders: {},
  credentials: 'include',
  api: (args) => authedApiFetch(args),
});
